import React from 'react';

import { Group, Select, Stack, Text } from '@mantine/core';
import type { PlotlyTypes } from 'visyn_core/plotly';
import { PlotlyComponent } from 'visyn_core/plotly';

import { CLONE_SIZE_CATEGORY_ORDER, getCdr3LengthTracesFromSeries } from './repertoire-analysis-utils';
import { CLONOTYPE_SIZE_OPTION, IG_ISOTYPE_OPTION, VH_FAMILY_OPTION } from '../constants/plot-options';
import type { BinnedData } from '../generated/generatedOpenAPI';

interface DistributionOfHeavyCdr3LengthsPlotProps {
  cdr3LengthDistributionData: BinnedData | null | undefined;
}

export default function DistributionOfHeavyCdr3LengthsPlot({ cdr3LengthDistributionData }: DistributionOfHeavyCdr3LengthsPlotProps) {
  const hasIgIsotypeData = Boolean(cdr3LengthDistributionData?.igIsotype?.length);
  const categorySelectorData = React.useMemo(() => {
    const data = [CLONOTYPE_SIZE_OPTION, VH_FAMILY_OPTION];
    if (hasIgIsotypeData) {
      data.push(IG_ISOTYPE_OPTION);
    }
    return data;
  }, [hasIgIsotypeData]);

  const [userCdr3DistributionCategoryOption, setUserCdr3DistributionCategoryOption] = React.useState<string | null>(null);

  const cdr3DistributionCategoryOption =
    userCdr3DistributionCategoryOption ?? (categorySelectorData.includes(IG_ISOTYPE_OPTION) ? IG_ISOTYPE_OPTION.value : CLONOTYPE_SIZE_OPTION.value);

  const data = React.useMemo(() => {
    if (cdr3DistributionCategoryOption === IG_ISOTYPE_OPTION.value) {
      return getCdr3LengthTracesFromSeries(cdr3LengthDistributionData?.igIsotype ?? []);
    }
    if (cdr3DistributionCategoryOption === VH_FAMILY_OPTION.value) {
      return getCdr3LengthTracesFromSeries(cdr3LengthDistributionData?.vhFamily ?? []);
    }
    if (cdr3DistributionCategoryOption === CLONOTYPE_SIZE_OPTION.value) {
      return getCdr3LengthTracesFromSeries(cdr3LengthDistributionData?.cloneSizeCategory ?? [], CLONE_SIZE_CATEGORY_ORDER);
    }

    return [];
  }, [cdr3DistributionCategoryOption, cdr3LengthDistributionData]);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text>Distribution of CDRH3 (heavy chain CDR3) sequence length (AAs)</Text>
        <Group gap={7}>
          <Text fw={400}>Distribution type:</Text>
          <Select
            data-testid="cdr3-distribution-category-selector"
            value={cdr3DistributionCategoryOption}
            onChange={(value) => {
              if (typeof value === 'string') {
                setUserCdr3DistributionCategoryOption(value);
              }
            }}
            data={categorySelectorData}
            w={150}
          />
        </Group>
      </Group>
      <div data-testid="heavy-chain-cdr3-distributions">
        <PlotlyComponent
          data={data as PlotlyTypes.Data[]}
          style={{ width: '80%' }}
          layout={{
            showlegend: true, // always show legend
            barmode: 'stack',
            yaxis: {
              zeroline: false,

              title: {
                text: 'Number of cells',
                font: { size: 16 },
              },
              layer: 'above traces',
            },
            xaxis: {
              zeroline: false,
              tickmode: 'linear',
              dtick: 1,
              title: {
                text: 'CDR3 sequence length',
                font: { size: 16 },
              },
              layer: 'above traces',
            },
            legend: {
              font: {
                family: 'Roboto, sans-serif',
                size: 14,
              },
            },
            bargap: 0.1,

            height: 550,
            margin: {
              l: 60,
              r: 10,
              b: 50,
              t: 20,
            },
          }}
          config={{
            displaylogo: false,
            modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'autoScale2d', 'select2d'],
          }}
        />
      </div>
    </Stack>
  );
}
